import { postUriBuilder } from 'pubky-app-specs';
import type { TDeletePostParams } from '@/controllers/post/post.types';
import { db } from '@/database/franky/franky';
import { DatabaseErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { ErrorService } from '@/libs/error/error.types';
import { isAppError } from '@/libs/error/error.utils';
import { HttpMethod } from '@/libs/http/http.types';
import { Logger } from '@/libs/logger/logger';
import { parseCollectionContent } from '@/libs/post/collectionContent';
import { getTtlPostMs } from '@/libs/runtime-config/runtime-config';
import { isPostDeleted } from '@/libs/utils/utils';
import { CompositeIdDomain } from '@/models/models.types';
import { buildCompositeIdFromPubkyUri, parseCompositeId } from '@/models/models.utils';
import { PostCountsModel } from '@/models/post/counts/postCounts';
import type { PostCountsModelSchema } from '@/models/post/counts/postCounts.schema';
import type { TPostCountsParams } from '@/models/post/counts/postCounts.types';
import { PostDetailsModel } from '@/models/post/details/postDetails';
import { DELETED } from '@/models/post/details/postDetails.constants';
import type { PostDetailsModelSchema } from '@/models/post/details/postDetails.schema';
import { PostRelationshipsModel } from '@/models/post/relationships/postRelationships';
import type { PostRelationshipsModelSchema } from '@/models/post/relationships/postRelationships.schema';
import { PostTagsModel } from '@/models/post/tags/postTags';
import { PostTtlModel } from '@/models/post/ttl/postTtl';
import {
  buildAuthorCollectionsStreamId,
  getPostStreamKind,
  type PostStreamId,
  PostStreamTypes,
} from '@/models/stream/post/postStream.types';
import { PostStreamModel } from '@/models/stream/post/tables/postStream';
import { UnreadPostStreamModel } from '@/models/stream/post/tables/postStream.unread';
import { UserCountsModel } from '@/models/user/counts/userCounts';
import { PostNormalizer } from '@/pipes/post/post.normalizer';
import type { TLocalSavePostParams, TLocalUpdatePostStreamParams } from '@/services/local/post/post.types';
import {
  type CollectionCountWrite,
  recentCollectionCounts,
  recentCollectionEnvelopes,
  type RecentCollectionWrites,
} from '@/services/local/post/recentCollectionCounts';

export class LocalPostService {
  private constructor() {}

  /**
   * Read a post from the local database.
   * @param postId - ID of the post to read
   * @returns Post details or null if not found
   */
  static async readDetails({ postId }: { postId: string }) {
    return PostDetailsModel.findById(postId);
  }

  /**
   * Bulk reads multiple post details from the local database, preserving input
   * order (undefined entries for posts not found). Mirrors `readRelationshipsByIds`.
   * @param postIds - Array of composite post IDs (author:postId)
   * @returns Array of post details aligned to `postIds` (undefined for missing posts)
   *
   * @throws {DatabaseError} When database operations fail
   */
  static async readDetailsByIds(postIds: string[]): Promise<(PostDetailsModelSchema | undefined)[]> {
    if (postIds.length === 0) return [];

    try {
      return await PostDetailsModel.findByIdsPreserveOrder(postIds);
    } catch (error) {
      Logger.error('Failed to read post details by ids', { postIds, error });
      throw Err.database(DatabaseErrorCode.QUERY_FAILED, 'Failed to read post details by ids', {
        service: ErrorService.Local,
        operation: 'readDetailsByIds',
        context: { postIds },
        cause: error,
      });
    }
  }

  /**
   * Filter out deleted posts from a list of post IDs.
   * Posts without details in cache are kept (fail-open semantics).
   *
   * @param postIds - Array of post IDs to filter
   * @returns Array of post IDs that are not deleted
   */
  static async filterDeletedPosts(postIds: string[]): Promise<string[]> {
    return PostDetailsModel.filterDeleted(postIds);
  }

  /**
   * Reads post counts for a specific post
   *
   * @param postId - Composite post ID (author:postId)
   * @returns Post counts or null if not found
   */
  static async readCounts(postId: string): Promise<PostCountsModelSchema | null> {
    return (await PostCountsModel.findById(postId)) ?? null;
  }

  /**
   * Reads post relationships for a specific post
   *
   * @param postId - Composite post ID (author:postId)
   * @returns Post relationships or null if not found
   */
  static async readRelationships(postId: string): Promise<PostRelationshipsModelSchema | null> {
    return PostRelationshipsModel.findById(postId);
  }

  /**
   * Reads post relationships for multiple posts
   *
   * @param postIds - Array of composite post IDs (author:postId)
   * @returns Array of post relationships (undefined entries for posts not found)
   *
   * @throws {DatabaseError} When database operations fail
   */
  static async readRelationshipsByIds(postIds: string[]): Promise<(PostRelationshipsModelSchema | undefined)[]> {
    try {
      return await PostRelationshipsModel.findByIdsPreserveOrder(postIds);
    } catch (error) {
      Logger.error('Failed to read post relationships by ids', { postIds, error });
      throw Err.database(DatabaseErrorCode.QUERY_FAILED, 'Failed to read post relationships by ids', {
        service: ErrorService.Local,
        operation: 'readRelationshipsByIds',
        context: { postIds },
        cause: error,
      });
    }
  }

  /**
   * Reads all posts that are replies to a specific post
   * @param postId - Composite post ID to read replies for
   * @returns Array of post relationships that replied to this post
   */
  static async readReplies(postId: string): Promise<PostRelationshipsModelSchema[]> {
    return PostRelationshipsModel.getReplies(postId);
  }

  static async updatePostCounts({ postCompositeId, countChanges }: TPostCountsParams) {
    await PostCountsModel.updateCounts({ postCompositeId, countChanges });
  }

  /**
   * Upserts a post TTL record so the post becomes stale again after `retryDelayMs`.
   * Used when Nexus omits a subscribed post (deleted, or not indexed yet) so the
   * coordinator retries it on a cooldown instead of every tick.
   *
   * The timestamp is calculated as: now - (postTtlMs - retryDelayMs). With
   * `unlessWrittenSince`, a row written at or after that time (a local edit, or
   * another successful refresh, landed while the batch was in flight) is kept, so
   * the cooldown never shortens real freshness. The check and the write share one
   * transaction.
   */
  static async upsertTtlWithDelay(
    compositePostId: string,
    retryDelayMs: number,
    options: { unlessWrittenSince?: number } = {},
  ): Promise<void> {
    const lastUpdatedAt = Date.now() - (getTtlPostMs() - retryDelayMs);
    await db.transaction('rw', PostTtlModel.table, async () => {
      if (options.unlessWrittenSince !== undefined) {
        const existing = await PostTtlModel.findById(compositePostId);
        if (existing && existing.lastUpdatedAt >= options.unlessWrittenSince) return;
      }
      await PostTtlModel.upsert({ id: compositePostId, lastUpdatedAt });
    });
  }

  /**
   * Edit a post's content (and optionally attachments/kind) in the local database.
   *
   * @param params.compositePostId - Composite post ID (author:postId)
   * @param params.content - New content for the post
   * @param params.attachments - New attachment URIs (`null` clears them); `undefined` leaves the column untouched
   * @param params.kind - New lowercase kind; `undefined` leaves the column untouched
   *
   * @throws {DatabaseError} When database operations fail
   */
  static async edit({
    compositePostId,
    content,
    attachments,
    kind,
  }: {
    compositePostId: string;
    content: string;
    attachments?: string[] | null;
    kind?: string;
  }) {
    // Set once the transaction announces its curated-count writes; runs if it does not commit.
    let restoreMarks: (() => void) | undefined;
    try {
      // `deleted: false` clears the tombstone flag on any write that restores
      // live content: `commitEdit` (and its rollback after a failed PUT) reuse
      // this method, and a row left flagged deleted would keep rendering as a
      // tombstone even though its content is back.
      const changes: Partial<PostDetailsModelSchema> = { content, deleted: false };
      if (attachments !== undefined) {
        changes.attachments = attachments;
      }
      if (kind !== undefined) {
        changes.kind = kind;
      }

      await db.transaction('rw', [PostDetailsModel.table, PostCountsModel.table, PostTtlModel.table], async () => {
        // Read before the write: the curated-item diff below needs the pre-edit envelope.
        const existing = await PostDetailsModel.findById(compositePostId);
        const now = Date.now();
        const nextKind = kind ?? existing?.kind;
        if (nextKind === 'collection') {
          changes.localUpdatedAt = now;
        }
        await PostDetailsModel.update(compositePostId, changes);
        // Touch TTL so the coordinator considers the edited post fresh and
        // doesn't overwrite the local edit with stale (pre-edit) Nexus data
        await PostTtlModel.upsert({ id: compositePostId, lastUpdatedAt: now });

        // A collection edit (item added/removed, or a kind flip in either direction)
        // moves the curated posts' `collections` count, like Nexus's COLLECTED edges.
        const curated = this.updateCuratedPostCounts(
          this.curatedItemIds(existing?.kind, existing?.content),
          this.curatedItemIds(nextKind, content),
          existing?.kind === 'collection' || nextKind === 'collection' ? compositePostId : undefined,
        );
        restoreMarks = curated.restoreMarks;
        await Promise.all(curated.ops);
      });
      Logger.debug('Post edited successfully', { compositePostId });
    } catch (error) {
      restoreMarks?.();
      // A model failure is already an AppError with its own code and context:
      // rethrow it unchanged (docs/error-handling.md); wrap only raw failures.
      if (isAppError(error)) throw error;
      throw Err.database(DatabaseErrorCode.WRITE_FAILED, 'Failed to edit post', {
        service: ErrorService.Local,
        operation: 'edit',
        context: { compositePostId },
        cause: error,
      });
    }
  }

  /**
   * Remove a post from every cached stream filtered to `kind`.
   *
   * Used after an edit changes a post's kind: the post would otherwise linger
   * in the old kind's filtered feeds (e.g. a text post sitting in the Images
   * tab after its image was removed) — permanently, since stream persistence
   * only merges and never evicts. Scans all cached stream ids and matches
   * their kind slot via the canonical `getPostStreamKind` parser, covering
   * every kind-bearing shape (timeline, wot, tagged, sorted-author "Me",
   * author-kind). The post is deliberately NOT inserted into the new kind's
   * streams locally — correct placement needs Nexus stream ordering.
   *
   * @throws {DatabaseError} When database operations fail
   */
  static async removeFromKindStreams({ compositePostId, kind }: { compositePostId: string; kind: string }) {
    try {
      const [streamIds, unreadStreamIds] = await Promise.all([
        PostStreamModel.table.toCollection().primaryKeys(),
        UnreadPostStreamModel.table.toCollection().primaryKeys(),
      ]);

      const matchesKind = (streamId: unknown) => getPostStreamKind(String(streamId)) === kind;

      await Promise.all([
        ...streamIds
          .filter(matchesKind)
          .map((streamId) => PostStreamModel.removeItems(streamId as PostStreamId, [compositePostId])),
        ...unreadStreamIds
          .filter(matchesKind)
          .map((streamId) => UnreadPostStreamModel.removeItems(streamId as PostStreamId, [compositePostId])),
      ]);
    } catch (error) {
      throw Err.database(DatabaseErrorCode.WRITE_FAILED, 'Failed to remove post from kind streams', {
        service: ErrorService.Local,
        operation: 'removeFromKindStreams',
        context: { compositePostId, kind },
        cause: error,
      });
    }
  }

  /**
   * Save a new post to the local database.
   *
   * Creates a new post with all its related records:
   * - Post details (content, kind, URI, etc.)
   * - Post counts (initialized to zero)
   * - Post relationships (parent URI if reply)
   * - Post tags (empty array)
   *
   * If the post is a reply, also updates the parent post's reply count.
   *
   * @param params.authorId - Unique identifier of the post author
   * @param params.postId - Unique identifier for the post
   * @param params.post - PubkyAppPost object
   *
   * @throws {DatabaseError} When database operations fail
   */
  static async create({ compositePostId, post }: TLocalSavePostParams) {
    const { content, kind, parent: parentUri, attachments, embed, lock } = post;

    const repostedUri = embed?.uri ?? null;
    const normalizedKind = PostNormalizer.postKindToLowerCase(kind);

    const { pubky: authorId, id: postId } = parseCompositeId(compositePostId);
    // Set once the transaction announces its curated-count writes; runs if it does not commit.
    let restoreMarks: (() => void) | undefined;

    try {
      const postDetails: PostDetailsModelSchema = {
        id: compositePostId,
        content,
        indexed_at: Date.now(),
        kind: normalizedKind,
        uri: postUriBuilder(authorId, postId),
        attachments: attachments ?? null,
        lock: lock ?? null,
      };
      if (normalizedKind === 'collection') {
        postDetails.localUpdatedAt = postDetails.indexed_at;
      }

      const postRelationships: PostRelationshipsModelSchema = {
        id: compositePostId,
        replied: parentUri ?? null,
        reposted: repostedUri,
        mentioned: [],
      };

      const postCounts: PostCountsModelSchema = {
        id: compositePostId,
        tags: 0,
        unique_tags: 0,
        replies: 0,
        reposts: 0,
        collections: 0,
      };

      await db.transaction(
        'rw',
        [
          PostDetailsModel.table,
          PostRelationshipsModel.table,
          PostCountsModel.table,
          PostTagsModel.table,
          UserCountsModel.table,
          PostStreamModel.table,
          PostTtlModel.table,
        ],
        async () => {
          await Promise.all([
            PostDetailsModel.create(postDetails),
            PostRelationshipsModel.create(postRelationships),
            PostCountsModel.create(postCounts),
            // A new post has no tags on Nexus yet. Seed an initialized, complete, fresh window
            // for its author so the first card mount does not force a tag request and the
            // TTL pass does not flag it immediately.
            PostTagsModel.create({
              id: compositePostId,
              tags: [],
              cache: { cursor: 0, exhausted: true, fetchedAt: Date.now(), revision: 0, viewerId: authorId },
            }),
          ]);

          const ops: Promise<unknown>[] = [];

          // Update related post counts
          if (parentUri) {
            ops.push(this.updatePostCount(parentUri, 'replies', 1));
            // Touch parent post TTL so the coordinator considers it fresh
            // and doesn't overwrite the updated reply count from Nexus
            const parentPostId = buildCompositeIdFromPubkyUri({
              uri: parentUri,
              domain: CompositeIdDomain.POSTS,
            });
            if (parentPostId) {
              ops.push(PostTtlModel.upsert({ id: parentPostId, lastUpdatedAt: Date.now() }));
            }
          }
          if (repostedUri) {
            ops.push(this.updatePostCount(repostedUri, 'reposts', 1));
            // Touch reposted post TTL so the coordinator considers it fresh
            // and doesn't overwrite the updated repost count from Nexus
            const repostedPostId = buildCompositeIdFromPubkyUri({
              uri: repostedUri,
              domain: CompositeIdDomain.POSTS,
            });
            if (repostedPostId) {
              ops.push(PostTtlModel.upsert({ id: repostedPostId, lastUpdatedAt: Date.now() }));
            }
          }

          // A new collection curates its items from the start: bump their `collections` count.
          const curated = this.updateCuratedPostCounts(
            new Set(),
            this.curatedItemIds(normalizedKind, content),
            normalizedKind === 'collection' ? compositePostId : undefined,
          );
          restoreMarks = curated.restoreMarks;
          ops.push(...curated.ops);

          // Touch TTL for the new post
          ops.push(
            PostTtlModel.upsert({ id: compositePostId, lastUpdatedAt: postDetails.localUpdatedAt ?? Date.now() }),
          );

          // Update author's user counts in a single operation.
          // A collection is a collection-kind post, so it bumps both the total
          // `posts` count and the dedicated `collections` count (the profile
          // sidebar subtracts collections back out of posts).
          ops.push(
            UserCountsModel.updateCounts({
              userId: authorId,
              countChanges: {
                posts: 1,
                replies: parentUri ? 1 : 0,
                collections: normalizedKind === 'collection' ? 1 : 0,
              },
            }),
          );

          this.updatePostStream({
            compositePostId,
            kind: normalizedKind,
            parentUri,
            ops,
            action: HttpMethod.PUT,
          });

          await Promise.all(ops);
        },
      );
    } catch (error) {
      restoreMarks?.();
      // A model failure is already an AppError with its own code and context:
      // rethrow it unchanged (docs/error-handling.md); wrap only raw failures.
      if (isAppError(error)) throw error;
      throw Err.database(DatabaseErrorCode.WRITE_FAILED, 'Failed to save post', {
        service: ErrorService.Local,
        operation: 'create',
        context: { compositePostId, kind },
        cause: error,
      });
    }
  }

  /**
   * Delete a post from the local database.
   *
   * Removes the post and all related records. If the post is a reply,
   * decrements the parent post's reply count. If the post is a repost,
   * decrements the original post's repost count.
   *
   * @param compositePostId - Composite post ID (author:postId)
   * @returns true if the post had connections (soft deleted), false otherwise
   */
  static async delete({ compositePostId }: TDeletePostParams): Promise<boolean> {
    const { pubky: authorId } = parseCompositeId(compositePostId);

    // Idempotency guard: if the post is already tombstoned (the `deleted` flag,
    // or the legacy `content === DELETED` sentinel), short-circuit. Otherwise a
    // stray re-delete would re-run the hard-delete transaction's
    // `UserCountsModel.updateCounts({ posts: -1 })` and drift the author's post
    // count. In normal UX this is unreachable — every render path for a
    // tombstoned post shows the deleted-state component with no delete button —
    // but the guard keeps the function idempotent against races and stale clients.
    const existing = await PostDetailsModel.findById(compositePostId);
    if (isPostDeleted(existing)) {
      Logger.warn('[LocalPostService.delete] post already tombstoned, skipping', { compositePostId });
      return false;
    }

    // TODO: There is an edge case where the post counts are not found, but the post is linked. This should be handled.
    const postCounts = await PostCountsModel.findById(compositePostId);
    // Set once a transaction announces its curated-count writes; runs if it does not commit.
    let restoreMarks: (() => void) | undefined;
    // If counts exist and post is linked → soft delete (tombstone, keep records)
    if (postCounts && this.isPostLinked(postCounts)) {
      try {
        await db.transaction('rw', [PostDetailsModel.table, PostCountsModel.table, PostTtlModel.table], async () => {
          // Re-read inside the transaction: the guard above ran outside it, so a
          // concurrent delete (a second tab) may have tombstoned the row and
          // released its curated memberships already. Diffing from that stale
          // `existing` would take the items' `collections` count below the truth.
          const current = await PostDetailsModel.findById(compositePostId);
          if (!current || isPostDeleted(current)) {
            Logger.warn('[LocalPostService.delete] post tombstoned by a concurrent delete, skipping', {
              compositePostId,
            });
            return;
          }
          await PostDetailsModel.update(compositePostId, { content: DELETED, deleted: true });
          // A tombstoned collection curates nothing any more (Nexus drops its COLLECTED edges too).
          const curated = this.updateCuratedPostCounts(this.curatedItemIds(current.kind, current.content), new Set());
          restoreMarks = curated.restoreMarks;
          await Promise.all([
            // The tombstone is a local write like any other: stamp its TTL.
            PostTtlModel.upsert({ id: compositePostId, lastUpdatedAt: Date.now() }),
            ...curated.ops,
          ]);
        });
      } catch (error) {
        restoreMarks?.();
        // A model failure is already an AppError with its own code and context:
        // rethrow it unchanged (docs/error-handling.md); wrap only raw failures.
        if (isAppError(error)) throw error;
        throw Err.database(DatabaseErrorCode.DELETE_FAILED, 'Failed to delete post', {
          service: ErrorService.Local,
          operation: 'delete',
          context: { compositePostId },
          cause: error,
        });
      }
      return true;
    }

    // Hard delete - proceed even if postCounts missing (treat as not linked)
    try {
      await db.transaction(
        'rw',
        [
          PostDetailsModel.table,
          PostRelationshipsModel.table,
          PostCountsModel.table,
          PostTagsModel.table,
          UserCountsModel.table,
          PostStreamModel.table,
          UnreadPostStreamModel.table,
          PostTtlModel.table,
        ],
        async () => {
          // Re-read inside the transaction (see the soft-delete branch): a concurrent
          // delete that tombstoned the row first already ran every decrement below.
          const postDetails = await PostDetailsModel.findById(compositePostId);
          if (isPostDeleted(postDetails)) {
            Logger.warn('[LocalPostService.delete] post tombstoned by a concurrent delete, skipping', {
              compositePostId,
            });
            return;
          }
          const postRelationships = await PostRelationshipsModel.findById(compositePostId);
          const parentUri = postRelationships?.replied ?? undefined;
          const repostedUri = postRelationships?.reposted ?? undefined;
          const kind = postDetails?.kind ?? 'short';

          await Promise.all([
            // Tombstone, not delete. The hard-delete branch used to drop
            // `PostDetails` entirely, but that left `useLocalFirstQuery`
            // consumers seeing `data === null` and racing the homeserver
            // DELETE: `fetchFn` would re-query Nexus, which still returned
            // the post (Nexus eventual consistency between the delete index
            // and the by-ids endpoint), and `persistPosts.bulkSave` wrote
            // the original content back. Leaving a `content = [DELETED]`
            // row makes `useLocalFirstQuery` treat the post as cache-hit
            // (data is non-null), so `fetchFn` never fires and the post
            // stays deleted. Auxiliary records (relationships, counts,
            // tags) still get fully removed below — only the details row
            // sticks around as a tombstone.
            PostDetailsModel.update(compositePostId, { content: DELETED, deleted: true }),
            PostRelationshipsModel.deleteById(compositePostId),
            PostCountsModel.deleteById(compositePostId),
            PostTagsModel.deleteById(compositePostId),
          ]);

          const ops: Promise<unknown>[] = [];

          // Decrement related post counts
          if (parentUri) {
            ops.push(this.updatePostCount(parentUri, 'replies', -1));
            // Touch parent post TTL so the coordinator considers it fresh
            // and doesn't overwrite the updated reply count from Nexus
            const parentPostId = buildCompositeIdFromPubkyUri({
              uri: parentUri,
              domain: CompositeIdDomain.POSTS,
            });
            if (parentPostId) {
              ops.push(PostTtlModel.upsert({ id: parentPostId, lastUpdatedAt: Date.now() }));
            }
          }
          if (repostedUri) {
            ops.push(this.updatePostCount(repostedUri, 'reposts', -1));
            // Touch reposted post TTL so the coordinator considers it fresh
            // and doesn't overwrite the updated repost count from Nexus
            const repostedPostId = buildCompositeIdFromPubkyUri({
              uri: repostedUri,
              domain: CompositeIdDomain.POSTS,
            });
            if (repostedPostId) {
              ops.push(PostTtlModel.upsert({ id: repostedPostId, lastUpdatedAt: Date.now() }));
            }
          }

          // The tombstone left behind is a local write like any other: stamp its TTL.
          ops.push(PostTtlModel.upsert({ id: compositePostId, lastUpdatedAt: Date.now() }));

          // A deleted collection curates nothing any more: its items lose one `collections` count.
          const curated = this.updateCuratedPostCounts(this.curatedItemIds(kind, postDetails?.content), new Set());
          restoreMarks = curated.restoreMarks;
          ops.push(...curated.ops);

          // Update author's user counts in a single operation. Mirror the create
          // path: a collection-kind post decrements both `posts` and `collections`.
          ops.push(
            UserCountsModel.updateCounts({
              userId: authorId,
              countChanges: {
                posts: -1,
                replies: parentUri ? -1 : 0,
                collections: kind === 'collection' ? -1 : 0,
              },
            }),
          );

          // Remove post from streams (including unread streams)
          this.updatePostStream({ compositePostId, kind, parentUri, ops, action: HttpMethod.DELETE });

          await Promise.all(ops);
        },
      );
      return false;
    } catch (error) {
      restoreMarks?.();
      // A model failure is already an AppError with its own code and context:
      // rethrow it unchanged (docs/error-handling.md); wrap only raw failures.
      if (isAppError(error)) throw error;
      throw Err.database(DatabaseErrorCode.DELETE_FAILED, 'Failed to delete post', {
        service: ErrorService.Local,
        operation: 'delete',
        context: { compositePostId },
        cause: error,
      });
    }
  }

  private static updatePostStream({ compositePostId, kind, parentUri, ops, action }: TLocalUpdatePostStreamParams) {
    const { pubky: authorId } = parseCompositeId(compositePostId);

    // Helper to call the appropriate method with proper class context
    const updateStream = (streamId: PostStreamId, items: string[]) => {
      if (action === HttpMethod.PUT) {
        return PostStreamModel.prependItems(streamId, items);
      } else {
        return PostStreamModel.removeItems(streamId, items);
      }
    };

    // Helper to remove from unread streams when deleting
    // This prevents ghost posts when a post is deleted while still in the unread stream
    const removeFromUnreadStream = (streamId: PostStreamId, items: string[]) => {
      if (action === HttpMethod.DELETE) {
        return UnreadPostStreamModel.removeItems(streamId, items);
      }
      return Promise.resolve();
    };

    if (parentUri) {
      const parentCompositeId = buildCompositeIdFromPubkyUri({
        uri: parentUri,
        domain: CompositeIdDomain.POSTS,
      });
      ops.push(updateStream(`author_replies:${authorId}`, [compositePostId]));
      ops.push(updateStream(`post_replies:${parentCompositeId}`, [compositePostId]));
    } else if (kind === 'collection') {
      ops.push(updateStream(buildAuthorCollectionsStreamId(authorId), [compositePostId]));
      ops.push(updateStream(PostStreamTypes.TIMELINE_ALL_COLLECTION, [compositePostId]));
      ops.push(updateStream(PostStreamTypes.TIMELINE_FOLLOWING_COLLECTION, [compositePostId]));
      ops.push(updateStream(PostStreamTypes.TIMELINE_FRIENDS_COLLECTION, [compositePostId]));
      ops.push(removeFromUnreadStream(buildAuthorCollectionsStreamId(authorId), [compositePostId]));
      ops.push(removeFromUnreadStream(PostStreamTypes.TIMELINE_ALL_COLLECTION, [compositePostId]));
      ops.push(removeFromUnreadStream(PostStreamTypes.TIMELINE_FOLLOWING_COLLECTION, [compositePostId]));
      ops.push(removeFromUnreadStream(PostStreamTypes.TIMELINE_FRIENDS_COLLECTION, [compositePostId]));
    } else {
      ops.push(updateStream(PostStreamTypes.TIMELINE_ALL_ALL, [compositePostId]));
      ops.push(updateStream(`timeline:all:${kind}` as PostStreamId, [compositePostId]));
      ops.push(updateStream(PostStreamTypes.TIMELINE_FOLLOWING_ALL, [compositePostId]));
      ops.push(updateStream(`timeline:following:${kind}` as PostStreamId, [compositePostId]));
      ops.push(updateStream(PostStreamTypes.TIMELINE_FRIENDS_ALL, [compositePostId]));
      ops.push(updateStream(`timeline:friends:${kind}` as PostStreamId, [compositePostId]));
      ops.push(updateStream(`author:${authorId}`, [compositePostId]));

      // Also remove from unread streams when deleting to prevent ghost posts
      // This handles the case where a post was polled and added to unread stream before deletion
      // Clean both the "all" streams and kind-specific streams since the user may have
      // been viewing a filtered feed (e.g., timeline:all:short) when the post was polled
      ops.push(removeFromUnreadStream(PostStreamTypes.TIMELINE_ALL_ALL, [compositePostId]));
      ops.push(removeFromUnreadStream(`timeline:all:${kind}` as PostStreamId, [compositePostId]));
      ops.push(removeFromUnreadStream(PostStreamTypes.TIMELINE_FOLLOWING_ALL, [compositePostId]));
      ops.push(removeFromUnreadStream(`timeline:following:${kind}` as PostStreamId, [compositePostId]));
      ops.push(removeFromUnreadStream(PostStreamTypes.TIMELINE_FRIENDS_ALL, [compositePostId]));
      ops.push(removeFromUnreadStream(`timeline:friends:${kind}` as PostStreamId, [compositePostId]));
    }
  }

  private static isPostLinked(postCounts: PostCountsModelSchema): boolean {
    return postCounts.replies > 0 || postCounts.reposts > 0 || postCounts.tags > 0;
  }

  /**
   * Composite ids of the posts a collection envelope curates; empty for any other kind and for
   * envelopes that do not parse (a tombstone, malformed content). Diffing on ids rather than
   * raw URIs keeps two spellings of one post from counting twice, and drops non-post URIs the
   * way Nexus does (it only links items that are posts).
   */
  private static curatedItemIds(kind: string | undefined, content: string | null | undefined): Set<string> {
    if (kind !== 'collection') return new Set();
    const itemIds = (parseCollectionContent(content)?.items ?? [])
      .map((uri) => buildCompositeIdFromPubkyUri({ uri, domain: CompositeIdDomain.POSTS }))
      .filter((itemId): itemId is string => itemId !== null);
    return new Set(itemIds);
  }

  /**
   * Local counterpart of Nexus's COLLECTED edges (pubky-nexus#1067): every post the collection
   * gained gets `collections + 1`, every post it dropped `collections - 1`. Each touched post is
   * marked in `recentCollectionCounts` before the write, like `recentUnbookmarks`, so a Nexus
   * count fetched before the edit is indexed does not undo the change (`persistPosts`), and its
   * TTL is stamped so the coordinator does not schedule a refresh for it right away (the
   * reply/repost count pattern above). With `envelopeId`, the collection being written is
   * marked in `recentCollectionEnvelopes` as well: its local envelope is the baseline the next
   * diff reads, and a copy fetched before this write is indexed would otherwise replace it,
   * uncheck the picker, and let the next save count the same membership a second time.
   * Returns the pending writes for the caller's transaction and `restoreMarks`, which the
   * caller runs if that transaction does not commit: the marks then go back to what they
   * were, so a rolled-back write never freezes a count or an envelope.
   */
  private static updateCuratedPostCounts(
    previousItemIds: Set<string>,
    nextItemIds: Set<string>,
    envelopeId?: string,
  ): { ops: Promise<unknown>[]; restoreMarks: () => void } {
    const ops: Promise<unknown>[] = [];
    const replacedMarks: Array<[RecentCollectionWrites, string, CollectionCountWrite | undefined]> = [];
    const bump = (postCompositeId: string, collections: number) => {
      replacedMarks.push([
        recentCollectionCounts,
        postCompositeId,
        recentCollectionCounts.markWritten(postCompositeId),
      ]);
      ops.push(PostCountsModel.updateCounts({ postCompositeId, countChanges: { collections } }));
      ops.push(PostTtlModel.upsert({ id: postCompositeId, lastUpdatedAt: Date.now() }));
    };

    for (const itemId of nextItemIds) {
      if (!previousItemIds.has(itemId)) bump(itemId, 1);
    }
    for (const itemId of previousItemIds) {
      if (!nextItemIds.has(itemId)) bump(itemId, -1);
    }
    if (envelopeId !== undefined) {
      replacedMarks.push([recentCollectionEnvelopes, envelopeId, recentCollectionEnvelopes.markWritten(envelopeId)]);
    }
    const restoreMarks = () => {
      for (const [registry, postCompositeId, previous] of replacedMarks) registry.restore(postCompositeId, previous);
    };
    return { ops, restoreMarks };
  }

  /**
   * Helper method to update post counts safely
   */
  private static async updatePostCount(
    uri: string,
    countField: 'replies' | 'reposts',
    countChange: number,
  ): Promise<void> {
    const postId = buildCompositeIdFromPubkyUri({ uri, domain: CompositeIdDomain.POSTS });
    if (!postId) return;

    const counts = await PostCountsModel.findById(postId);
    if (!counts) return;

    const currentCount = counts[countField];
    const newCount = Math.max(0, currentCount + countChange);

    await PostCountsModel.update(postId, { [countField]: newCount });
  }
}
